import React, { ComponentProps, FC, useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";
import Select, { OnChangeValue } from "react-select";
import { mapMessages } from "shared/mapMessages";

const messages = mapMessages("shared.components.ReactSelect", {
    noOptionsDefault: "No options",
});

export interface ISelectOption {
    label: string;
    value: string | number;
}

export function isSelectOption(value: OnChangeValue<ISelectOption, false>): value is ISelectOption {
    return typeof ((value as ISelectOption).value) !== "undefined";
}

export interface IReactSelectProps {
    className?: string;
    isClearable?: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    isSearchable: boolean;
    value?: string | number;
    options: ISelectOption[] | null;
    placeholder?: string;
    inputId?: string;
    "aria-label"?: string;
    "aria-required"?: boolean;
    "aria-invalid"?: boolean;
    "aria-errormessage"?: string;
    noOptionsMessage?: MessageDescriptor;
    name?: string;
    innerRef?: ComponentProps<typeof Select>["ref"];
}

export interface IReactSelectCallbackProps {
    onBlur?: () => void;
    onChange?: (value: string | number) => void;
    onSelected: (value: OnChangeValue<ISelectOption, false>) => void;
}

type Props = IReactSelectProps & IReactSelectCallbackProps;

export const ReactSelect: FC<Props> = ({
    options,
    placeholder,
    value,
    noOptionsMessage,
    onSelected,
    onBlur,
    onChange,
    innerRef,
    ...rest
}) => {
    const { formatMessage } = useIntl();

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    return (
        <Select
            value={getCurrentValue()}
            options={options || []}
            onBlur={onBlur}
            onInputChange={onInputChange}
            onChange={onSelectionChange}
            onKeyDown={onKeyDown}
            onMenuClose={onMenuClose}
            onMenuOpen={onMenuOpen}
            menuIsOpen={isMenuOpen}
            getOptionLabel={getOptionLabel}
            placeholder={placeholder}
            styles={{
                menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                }),
                placeholder: (provided) => ({
                    ...provided,
                    color: "#707070",
                }),
            }}
            noOptionsMessage={getNoOptionsMessage}
            ref={innerRef}
            {...rest}
        />
    );

    function onInputChange(newValue: string | number) {
        if (onChange) {
            onChange(newValue);
        }
    }

    function onSelectionChange(option: ISelectOption) {
        onSelected(option);
    }

    function getCurrentValue() {
        return (options && options.find(option => option.value === value)) || null;
    }

    function getOptionLabel(option: ISelectOption) {
        return option.label;
    }

    function getNoOptionsMessage() {
        return formatMessage(noOptionsMessage ?? messages.noOptionsDefault);
    }

    function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
        if (event.key === "Enter" && !isMenuOpen) {
            event.nativeEvent.preventDefault();
            onMenuOpen();
        }
    }

    function onMenuOpen() {
        setIsMenuOpen(true);
    }

    function onMenuClose() {
        setIsMenuOpen(false);
    }
};
